// Therapist-level default board styling, applied to every new board so the
// therapist doesn't reset it each session (issue #197). Stored per-device in
// localStorage.
import { Ambiance } from "../domain/types";

// Colon-separated on purpose. The i18n guard scans string literals and cannot
// tell a storage key shaped like `constellation.default.shape` from one the UI
// asks i18next for, so a dotted key here reads as a missing translation.
const SHAPE_KEY = "constellation:defaults:shape";
const COLOR_KEY = "constellation:defaults:color";
const AMBIANCE_KEY = "constellation:defaults:ambiance";

// What the first two were called before that rename, read once so defaults set
// under the old names survive it. Assembled rather than written out for the
// same reason the new names are not dotted.
const legacyKey = (name: string) => ["constellation", "default", name].join(".");

/** Scenes offered to the therapist, in the order they read best. */
export const AMBIANCES: Ambiance[] = ["bamboo", "base", "day", "night", "rainy"];

// A panorama that comes with a ground of its own. Applied when it is picked —
// the pale cyan default read as a swimming pool under the bamboo — and the
// colour picker still overrides it afterwards.
export const AMBIANCE_FLOOR: Partial<Record<Ambiance, string>> = {
    bamboo: "#dfead2",
};

/** The grove is the calm one, so it is what a therapist gets before they
 *  choose anything (issue #197). */
export const DEFAULT_AMBIANCE: Ambiance = "bamboo";

export const DEFAULT_TABLE_COLOR = "#dcf5f4";

export interface BoardDefaults {
    shape: "circle" | "square";
    color: string;
    ambiance: Ambiance;
}

function fallbackDefaults(): BoardDefaults {
    return {
        shape: "circle",
        color: AMBIANCE_FLOOR[DEFAULT_AMBIANCE] ?? DEFAULT_TABLE_COLOR,
        ambiance: DEFAULT_AMBIANCE,
    };
}

export function getBoardDefaults(): BoardDefaults {
    const fallback = fallbackDefaults();
    try {
        const shape =
            localStorage.getItem(SHAPE_KEY) ?? localStorage.getItem(legacyKey("shape"));
        const color =
            localStorage.getItem(COLOR_KEY) ?? localStorage.getItem(legacyKey("color"));
        const ambiance = localStorage.getItem(AMBIANCE_KEY) as Ambiance | null;
        return {
            shape: shape === "square" ? "square" : "circle",
            color: color || fallback.color,
            ambiance:
                ambiance && AMBIANCES.includes(ambiance) ? ambiance : fallback.ambiance,
        };
    } catch {
        return fallback;
    }
}

export function setBoardDefaults(defaults: BoardDefaults): void {
    try {
        localStorage.setItem(SHAPE_KEY, defaults.shape);
        localStorage.setItem(COLOR_KEY, defaults.color);
        localStorage.setItem(AMBIANCE_KEY, defaults.ambiance);
    } catch {
        // Storage can be unavailable (private mode); defaults simply don't stick.
    }
}
